import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { SUPPLIERS_QUERY_KEYS } from "../configs/queryKeys";
import { SupplierService } from "../services/suppliers.service";
import type { GetSuppliersParams } from "../services/suppliers.service.types";
import type { Supplier, SupplierResponse } from "../types/suppliers.types";

export const useSuppliersQuery = (params: GetSuppliersParams = {}) => {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: SUPPLIERS_QUERY_KEYS.list(params),
    queryFn: ({ pageParam = 1 }) =>
      SupplierService.getSuppliers({ ...params, page: pageParam }),
    getNextPageParam: (lastPage: SupplierResponse) => {
      const { current_page, total_pages } = lastPage.metadata;
      return current_page < total_pages ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
  });

  const suppliers: Supplier[] = data?.pages.flatMap((page) => page.suppliers) ?? [];
  const metadata = data?.pages[0]?.metadata;

  return {
    suppliers,
    metadata,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useSupplierQuery = (id: string, fields?: string) => {
  return useQuery({
    queryKey: SUPPLIERS_QUERY_KEYS.detail(id, fields),
    queryFn: () => SupplierService.getSupplier(id, fields),
    enabled: !!id,
  });
};
